<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue'

import { api, messageFrom } from '../api'
import { formatDate } from '../format'
import type { LibraryDocument } from '../types'

const emit = defineEmits<{ navigate: [path: string] }>()
const documents = ref<LibraryDocument[]>([])
const title = ref('')
const file = ref<File | null>(null)
const fileInput = ref<HTMLInputElement | null>(null)
const loading = ref(true)
const saving = ref(false)
const error = ref('')
const openDocumentId = ref<string | null>(null)
const openDocumentTitle = ref('')
const fullText = ref('')
const textLoading = ref(false)
const textError = ref('')
let pollTimer: number | null = null

function statusText(document: LibraryDocument): string {
  if (document.status === 'queued') return 'В очереди'
  if (document.status === 'processing') return 'Обрабатывается'
  if (document.status === 'ready') return 'Готово'
  return 'Ошибка'
}

function chooseFile(event: Event): void {
  const input = event.target as HTMLInputElement
  file.value = input.files?.[0] ?? null
  if (file.value && !title.value.trim()) {
    title.value = file.value.name.replace(/\.(?:txt|docx)$/iu, '')
  }
}

async function load(silent = false): Promise<void> {
  if (!silent) loading.value = true
  try {
    documents.value = (await api<{ documents: LibraryDocument[] }>('/api/admin/library')).documents
  } catch (reason) {
    if (!silent) error.value = messageFrom(reason)
  } finally {
    if (!silent) loading.value = false
  }
}

async function upload(): Promise<void> {
  if (!file.value || !title.value.trim() || saving.value) return
  saving.value = true
  error.value = ''
  try {
    const form = new FormData()
    form.append('title', title.value.trim())
    form.append('file', file.value, file.value.name)
    await api<{ document: LibraryDocument }>('/api/admin/library', {
      method: 'POST',
      body: form,
    })
    title.value = ''
    file.value = null
    if (fileInput.value) fileInput.value.value = ''
    await load(true)
  } catch (reason) {
    error.value = messageFrom(reason)
  } finally {
    saving.value = false
  }
}

async function remove(document: LibraryDocument): Promise<void> {
  if (!window.confirm(`Удалить «${document.title}» и все его фрагменты?`)) return
  error.value = ''
  try {
    await api<{ ok: true }>(`/api/admin/library/${encodeURIComponent(document.id)}`, {
      method: 'DELETE',
      body: JSON.stringify({ confirmed: true }),
    })
    if (openDocumentId.value === document.id) closeText()
    await load(true)
  } catch (reason) {
    error.value = messageFrom(reason)
  }
}

async function showText(document: LibraryDocument): Promise<void> {
  openDocumentId.value = document.id
  openDocumentTitle.value = document.title
  fullText.value = ''
  textError.value = ''
  textLoading.value = true
  try {
    fullText.value = (await api<{ text: string }>(
      `/api/admin/library/${encodeURIComponent(document.id)}/text`,
    )).text
  } catch (reason) {
    textError.value = messageFrom(reason)
  } finally {
    textLoading.value = false
  }
}

function closeText(): void {
  openDocumentId.value = null
  openDocumentTitle.value = ''
  fullText.value = ''
  textError.value = ''
}

onMounted(() => {
  void load()
  pollTimer = window.setInterval(() => {
    if (documents.value.some(document => document.status === 'queued' || document.status === 'processing')) {
      void load(true)
    }
  }, 3_000)
})

onBeforeUnmount(() => {
  if (pollTimer !== null) window.clearInterval(pollTimer)
})
</script>

<template>
  <main class="page page--library">
    <button class="back-link" type="button" @click="emit('navigate', '/admin')">← Все папки</button>
    <header class="page-heading">
      <p class="eyebrow">Отдельный источник знаний</p>
      <h1>Литература</h1>
    </header>

    <form class="library-upload" @submit.prevent="upload">
      <label for="library-title">Название документа</label>
      <input id="library-title" v-model="title" maxlength="240" placeholder="Например: Книга сообщества" />
      <label for="library-file">Файл TXT или DOCX</label>
      <input
        id="library-file"
        ref="fileInput"
        type="file"
        accept=".txt,.docx,text/plain,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        @change="chooseFile"
      />
      <p class="settings-hint">PDF не поддерживается. Обработка и эмбеддинги выполняются в фоне.</p>
      <button class="button button--primary" type="submit" :disabled="saving || !file || !title.trim()">
        {{ saving ? 'Загружаем…' : 'Загрузить' }}
      </button>
      <p v-if="error" class="field-error" role="alert">{{ error }}</p>
    </form>

    <p v-if="loading" class="quiet-message">Загружаем список…</p>
    <p v-else-if="!documents.length" class="empty-state">Литература ещё не загружена.</p>
    <section v-else class="library-list">
      <article v-for="document in documents" :key="document.id" class="library-item">
        <div class="library-item__main">
          <strong>{{ document.title }}</strong>
          <span class="status" :class="`status--${document.status}`">{{ statusText(document) }}</span>
          <p>{{ document.sourceFilename }}</p>
          <p>
            {{ document.chunkCount }} фрагментов · {{ document.charCount.toLocaleString('ru-RU') }} знаков ·
            {{ formatDate(document.createdAt) }}
          </p>
          <p v-if="document.error" class="recording-error">{{ document.error }}</p>
        </div>
        <div class="row-actions">
          <button type="button" @click="showText(document)">Открыть текст</button>
          <button class="danger-link" type="button" @click="remove(document)">Удалить</button>
        </div>
      </article>
    </section>

    <section v-if="openDocumentId" class="library-reader">
      <div class="section-heading">
        <div>
          <p class="eyebrow">Полный текст · только админка</p>
          <h2>{{ openDocumentTitle }}</h2>
        </div>
        <button class="button button--quiet" type="button" @click="closeText">Закрыть</button>
      </div>
      <p v-if="textLoading" class="quiet-message">Открываем текст…</p>
      <p v-else-if="textError" class="field-error" role="alert">{{ textError }}</p>
      <pre v-else class="library-reader__text">{{ fullText }}</pre>
    </section>
  </main>
</template>
